import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BugReportIcon from '@mui/icons-material/BugReport';
import axios from 'axios';
import ExportDebugData from '../sr89Debug/ExportDebugData';

interface Ni63DebugFormData {
  calculationId: string;
}

interface Ni63DebugResults {
  calculationId: number;
  isotope: string;
  method: string;
  
  // Datos de entrada
  inputData: {
    cpm_muestra: number;
    cpm_blanco: number;
    tiempo_contaje_muestra: number;
    cantidad_vial_muestra_ml: number;
    contaje_por_uno: number;
    constante_desintegracion: number;
    tiempo_incremento_min: number;
  };
  
  // Eficiencia
  eficiencia: number;
  eficienciaComponents: any;
  
  // CPM Neto
  cpmNeto: number;
  cpmNetoComponents: any;
  
  // Factor Exponencial
  factorExponencial: number;
  factorExponencialComponents: any;
  
  // Actividad
  activity: number;
  activityComponents: any;
  
  // Componentes de incertidumbre
  uncertaintyComponents: any;
  
  // Desglose de incRelSquaredPerformance
  incRelSquaredPerformance: number;
  incRelSquaredPerformanceComponents: any;
  
  // Desglose de incRelSquaredSampleVialQuantity
  incRelSquaredSampleVialQuantity: number;
  incRelSquaredSampleVialQuantityComponents: any;
  
  // Límite Crítico
  limiteCritico: number;
  limiteCriticoComponents: any;
  
  // LID
  lid: number;
  lidComponents: any;
  
  // Incertidumbre
  uncertainty: number;
  
  // Resultados finales
  finalResults: any;
  
  // Datos de patrones
  primaryPatternData: any;
  secondaryPatternData: any;
  tertiaryPatternData: any;
  
  // Datos de calibración
  calibrationData: any;
  
  // Incertidumbres relativas
  relativeUncertainties: any;
  
  // Datos de CalibrationUncertainty
  calibrationUncertaintyData: any;
}

const Ni63DebugPage: React.FC = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<Ni63DebugResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calculationId, setCalculationId] = useState<string>('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!calculationId) {
      setError('Por favor ingrese un ID de cálculo válido');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResults(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`http://localhost:8000/api/debug/ni63/${calculationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setResults(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al obtener los datos de depuración');
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (title: string, data: Array<{ label: string; value: any; formula?: string; components?: any }>) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary">
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {data.map((item, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                  {item.label}
                </Typography>
                {item.formula && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                    {item.formula}
                  </Typography>
                )}
                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ my: 1 }}>
                  {typeof item.value === 'number' ? item.value.toExponential(6) : item.value}
                </Typography>
                
                {/* Mostrar componentes si existen */}
                {item.components && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      📊 Componentes del cálculo:
                    </Typography>
                    <Grid container spacing={1}>
                      {Object.entries(item.components).map(([key, value]: [string, any]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {key.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                              {typeof value === 'number' ? value.toExponential(4) : value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BugReportIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Debug Ni-63 (ISO 11929)
              </Typography>
              <Typography variant="subtitle1">
                Análisis ultra-detallado de cálculos de actividad, incertidumbre y LID
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/debug')}
            sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
          >
            Volver
          </Button>
        </Box>
      </Paper>

      {/* Formulario de búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="ID del Cálculo"
                  type="number"
                  required
                  value={calculationId}
                  onChange={(e) => setCalculationId(e.target.value)}
                  helperText="Ingrese el ID del cálculo que desea analizar"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || !calculationId}
                  startIcon={loading ? <CircularProgress size={20} /> : <BugReportIcon />}
                >
                  {loading ? 'Analizando...' : 'Analizar'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Resultados */}
      {results && (
        <>
          {/* Información del cálculo */}
          <Card sx={{ mb: 2, bgcolor: 'info.light' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">ID de Cálculo</Typography>
                  <Typography variant="h6" fontWeight="bold">{results.calculationId}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Isótopo</Typography>
                  <Typography variant="h6" fontWeight="bold">{results.isotope}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">Método</Typography>
                  <Typography variant="h6" fontWeight="bold">{results.method}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Botones de exportación */}
          <ExportDebugData results={results as any} calculationId={calculationId} />

          {/* Sección 1: Datos de Entrada */}
          {renderSection('1. Datos de Entrada', [
            { label: 'CPM Muestra', value: results.inputData.cpm_muestra },
            { label: 'CPM Blanco', value: results.inputData.cpm_blanco },
            { label: 'Tiempo Contaje Muestra (min)', value: results.inputData.tiempo_contaje_muestra },
            { label: 'Cantidad Vial Muestra (mL)', value: results.inputData.cantidad_vial_muestra_ml },
            { label: 'Contaje por Uno', value: results.inputData.contaje_por_uno },
            { label: 'Constante Desintegración', value: results.inputData.constante_desintegracion },
            { label: 'Tiempo Incremento (min)', value: results.inputData.tiempo_incremento_min },
          ])}

          {/* Sección 2: Eficiencia */}
          {renderSection('2. Eficiencia de Detección', [
            { 
              label: 'Eficiencia', 
              value: results.eficiencia,
              formula: results.eficienciaComponents?.formula,
              components: results.eficienciaComponents
            },
          ])}

          {/* Sección 3: CPM Neto */}
          {renderSection('3. CPM Neto', [
            { 
              label: 'CPM Neto', 
              value: results.cpmNeto,
              formula: results.cpmNetoComponents?.formula,
              components: results.cpmNetoComponents
            },
          ])}

          {/* Sección 4: Factor Exponencial */}
          {renderSection('4. Factor Exponencial (Decay)', [
            { 
              label: 'Factor Exponencial', 
              value: results.factorExponencial,
              formula: results.factorExponencialComponents?.formula,
              components: results.factorExponencialComponents
            },
          ])}

          {/* Sección 5: Actividad */}
          {renderSection('5. Cálculo de Actividad', [
            { 
              label: 'Actividad (Bq)', 
              value: results.activity,
              formula: results.activityComponents?.formula,
              components: results.activityComponents
            },
          ])}

          {/* Sección 6: Componentes de Incertidumbre */}
          {renderSection('6. Componentes de Incertidumbre', [
            { label: 'Inc² Contaje', value: results.uncertaintyComponents?.inc_cuadrado_contaje },
            { label: 'Inc² Rel Rendimiento', value: results.uncertaintyComponents?.inc_rel_cuadrado_rendimiento },
            { label: 'Inc² Rel Cantidad Vial', value: results.uncertaintyComponents?.inc_rel_cuadrado_cantidad_vial },
            { label: 'Inc² Inversa Eficiencia', value: results.uncertaintyComponents?.inc_cuadrado_inversa_eficiencia },
            { label: 'Inc² Factor Decay', value: results.uncertaintyComponents?.inc_cuadrado_factor_decay },
          ])}

          {/* Sección 6b: Desglose Ultra-detallado de Inc² Rel Rendimiento */}
          {results.incRelSquaredPerformanceComponents && renderSection('6b. Desglose Ultra-detallado: Inc² Rel Rendimiento (Performance)', [
            { 
              label: 'Inc² Rel Rendimiento Total', 
              value: results.incRelSquaredPerformance,
              formula: results.incRelSquaredPerformanceComponents?.formula,
              components: results.incRelSquaredPerformanceComponents
            },
          ])}

          {/* Sección 6c: Desglose Ultra-detallado de Inc² Rel Cantidad Vial */}
          {results.incRelSquaredSampleVialQuantityComponents && renderSection('6c. Desglose Ultra-detallado: Inc² Rel Cantidad Vial Muestra', [
            { 
              label: 'Inc² Rel Cantidad Vial Total', 
              value: results.incRelSquaredSampleVialQuantity,
              formula: results.incRelSquaredSampleVialQuantityComponents?.formula,
              components: results.incRelSquaredSampleVialQuantityComponents
            },
          ])}

          {/* Sección 7: Límite Crítico */}
          {renderSection('7. Límite Crítico', [
            { 
              label: 'Límite Crítico', 
              value: results.limiteCritico,
              formula: results.limiteCriticoComponents?.formula,
              components: results.limiteCriticoComponents
            },
          ])}

          {/* Sección 8: LID */}
          {renderSection('8. Límite de Detección (LID)', [
            { 
              label: 'LID (Bq)', 
              value: results.lid,
              formula: results.lidComponents?.formula,
              components: results.lidComponents
            },
          ])}

          {/* Sección 9: Incertidumbre */}
          {renderSection('9. Incertidumbre', [
            { 
              label: 'Incertidumbre (Bq)', 
              value: results.uncertainty,
              formula: results.uncertaintyComponents?.formula,
              components: results.uncertaintyComponents
            },
          ])}

          {/* Sección 10: Resultados Finales */}
          {renderSection('10. Resultados Finales Comparativos', [
            { label: 'Actividad (Calculada)', value: results.finalResults.actividad_bq },
            { label: 'Actividad (from Results)', value: results.finalResults.actividad_from_results },
            { label: 'Incertidumbre (Calculada)', value: results.finalResults.incertidumbre_bq },
            { label: 'Incertidumbre (from Results)', value: results.finalResults.incertidumbre_from_results },
            { label: 'LID (Calculado)', value: results.finalResults.lid_bq },
            { label: 'LID (from Results)', value: results.finalResults.lid_from_results },
          ])}

          {/* Sección 11: Datos Patrón Primario */}
          {renderSection('11. Datos del Patrón Primario', [
            { label: 'Actividad Patrón Primario', value: results.primaryPatternData.actividad_patron_primario },
            { label: 'Incertidumbre Patrón Primario', value: results.primaryPatternData.incertidumbre_patron_primario },
            { label: 'Masa Patrón Primario (mpp)', value: results.primaryPatternData.masa_patron_primario_mpp },
            { label: 'Incertidumbre Masa Patrón', value: results.primaryPatternData.incertidumbre_masa_patron_primario },
          ])}

          {/* Sección 12: Datos Patrón Secundario */}
          {renderSection('12. Datos del Patrón Secundario', [
            { label: 'Masa Patrón Secundario', value: results.secondaryPatternData.masa_patron_secundario },
            { label: 'Incertidumbre Masa Patrón', value: results.secondaryPatternData.incertidumbre_masa_patron_secundario },
          ])}

          {/* Sección 13: Datos Patrón Terciario */}
          {renderSection('13. Datos del Patrón Terciario', [
            { label: 'Masa Patrón Terciario', value: results.tertiaryPatternData.masa_patron_terciario },
            { label: 'Incertidumbre Masa Patrón', value: results.tertiaryPatternData.incertidumbre_masa_patron_terciario },
          ])}

          {/* Sección 14: Datos de Calibración */}
          {renderSection('14. Datos de Calibración', [
            { label: 'Masa Mayor Cantidad Centelleador', value: results.calibrationData.masa_mayor_cantidad_centelleador },
            { label: 'Inc Masa Mayor Cantidad', value: results.calibrationData.inc_masa_mayor_cantidad },
            { label: 'TSIE SQP Patrón sin Agente', value: results.calibrationData.tsie_sqp_patron_sin_agente },
            { label: 'Inc TSIE SQP Patrón', value: results.calibrationData.inc_tsie_sqp_patron },
            { label: 'CPM FC Mayor Cantidad', value: results.calibrationData.cpm_fc_mayor_cantidad },
            { label: 'Inc Contaje² FC', value: results.calibrationData.inc_contaje_cuadrado_fc },
            { label: 'Actividad Fuente Calibración', value: results.calibrationData.actividad_fuente_calibracion },
            { label: 'Inc² Actividad Fuente', value: results.calibrationData.inc_cuadrado_actividad_fuente },
          ])}

          {/* Sección 15: Incertidumbres Relativas */}
          {renderSection('15. Incertidumbres Relativas', [
            { label: 'Incertidumbre Relativa', value: results.relativeUncertainties.incertidumbre_relativa },
            { label: 'Inc² Rel Inversa Eficiencia', value: results.relativeUncertainties.inc_cuadrado_rel_inversa_eficiencia },
            { label: 'Inc² Contaje', value: results.relativeUncertainties.inc_cuadrado_contaje },
            { label: 'Inc² Rel Cantidad Vial', value: results.relativeUncertainties.inc_rel_cuadrado_cantidad_vial },
            { label: 'Inc² Rel Rendimiento', value: results.relativeUncertainties.inc_rel_cuadrado_rendimiento },
            { label: 'Inc² Factor Decay', value: results.relativeUncertainties.inc_cuadrado_factor_decay },
          ])}

          {/* Sección 16: Datos de CalibrationUncertainty */}
          {results.calibrationUncertaintyData && !results.calibrationUncertaintyData.error && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  16. Datos de CalibrationUncertainty
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* Información general */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  📋 Información General
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">ID</Typography>
                      <Typography variant="body1" fontWeight="bold">{results.calibrationUncertaintyData.id}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Fecha</Typography>
                      <Typography variant="body1" fontWeight="bold">{results.calibrationUncertaintyData.date}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Tipo Incertidumbre</Typography>
                      <Typography variant="body1" fontWeight="bold">{results.calibrationUncertaintyData.tipo_incertidumbre}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Incertidumbres de masa de patrones */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  🎯 Incertidumbres de Masa de Patrones
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">EUMPP</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.eumpp?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">EUMPS</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.eumps?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Inc. Masa Patrón Terciario</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.mass_uncertainty_tertiary_pattern?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Inc. Fuente Pat. Terc.</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.uncertainty_source_pat_terc?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Coeficientes AA, NI, FE */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  🧪 Coeficientes de Absorción Atómica, Níquel y Hierro
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">AA K2</Typography>
                      <Typography variant="body2" fontWeight="bold">{results.calibrationUncertaintyData.AA_k2?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">AA K1</Typography>
                      <Typography variant="body2" fontWeight="bold">{results.calibrationUncertaintyData.AA_k1?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">NI K2</Typography>
                      <Typography variant="body2" fontWeight="bold">{results.calibrationUncertaintyData.NI_k2?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">NI K1</Typography>
                      <Typography variant="body2" fontWeight="bold">{results.calibrationUncertaintyData.NI_k1?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">FE K2</Typography>
                      <Typography variant="body2" fontWeight="bold">{results.calibrationUncertaintyData.FE_k2?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">FE K1</Typography>
                      <Typography variant="body2" fontWeight="bold">{results.calibrationUncertaintyData.FE_k1?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Incertidumbres de volumen de agua */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  💧 Incertidumbres de Volumen de Agua (diferentes volúmenes)
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {[
                    { label: '1mL K2 (mL)', value: results.calibrationUncertaintyData.volume_water_1ml_k2_ml },
                    { label: '1mL K1 (g²)', value: results.calibrationUncertaintyData.volume_water_1ml_k1_g_2 },
                    { label: '5mL K2 (mL)', value: results.calibrationUncertaintyData.volume_water_5ml_k2_ml },
                    { label: '5mL K1 (g²)', value: results.calibrationUncertaintyData.volume_water_5ml_k1_g_2 },
                    { label: '25mL K2 (mL)', value: results.calibrationUncertaintyData.volume_water_25ml_k2_ml },
                    { label: '25mL K1 (g²)', value: results.calibrationUncertaintyData.volume_water_25ml_k1_g_2 },
                    { label: '500mL K2 (mL)', value: results.calibrationUncertaintyData.volume_water_500ml_k2_ml },
                    { label: '500mL K1 (g²)', value: results.calibrationUncertaintyData.volume_water_500ml_k1_g_2 },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{item.value?.toExponential(4) || 'N/A'}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Incertidumbres de masa de sólidos */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  🏔️ Incertidumbres de Masa de Sólidos
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {[
                    { label: '5mL K2 (mg)', value: results.calibrationUncertaintyData.mass_solid_5ml_k2_mg },
                    { label: '5mL K1 (g²)', value: results.calibrationUncertaintyData.mass_solid_5ml_k1_g_2 },
                    { label: '200g K2 (mg)', value: results.calibrationUncertaintyData.mass_solid_200g_k2_mg },
                    { label: '200g K1 (g²)', value: results.calibrationUncertaintyData.mass_solid_200g_k1_g_2 },
                    { label: '<200g K2 (mg)', value: results.calibrationUncertaintyData.mass_solid_minor_200g_k2_mg },
                    { label: '<200g K1 (g²)', value: results.calibrationUncertaintyData.mass_solid_minor_200g_k1_g_2 },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={2} key={index}>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{item.value?.toExponential(4) || 'N/A'}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Incertidumbres de masa de muestra */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  📊 Incertidumbres de Masa de Muestra
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {[
                    { label: 'US K2 (mg)', value: results.calibrationUncertaintyData.mass_sample_us_k2_mg },
                    { label: 'U²S K1 (g²)', value: results.calibrationUncertaintyData.mass_sample_u2s_k1_g2 },
                    { label: 'UB K2 (mg)', value: results.calibrationUncertaintyData.mass_sample_ub_k2_mg },
                    { label: 'U²B K1 (g²)', value: results.calibrationUncertaintyData.mass_sample_u2b_k1_g2 },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                        <Typography variant="body2" fontFamily="monospace">{item.value?.toExponential(4) || 'N/A'}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Incertidumbre de volumen de muestra */}
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                  🧬 Incertidumbre de Volumen de Muestra
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">K2 (L)</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.inc_vol_sample_k2_l?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">K1 U² (m³²)</Typography>
                      <Typography variant="body2" fontFamily="monospace">{results.calibrationUncertaintyData.inc_vol_sample_k1_u2_m3_2?.toExponential(4) || 'N/A'}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Comentarios */}
                {results.calibrationUncertaintyData.internal_comments && (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2 }}>
                      💬 Comentarios Internos
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2">{results.calibrationUncertaintyData.internal_comments}</Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Mostrar error si no se encontró CalibrationUncertainty */}
          {results.calibrationUncertaintyData?.error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {results.calibrationUncertaintyData.error}
              </Typography>
              {results.calibrationUncertaintyData.message && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {results.calibrationUncertaintyData.message}
                </Typography>
              )}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default Ni63DebugPage;
